import type { ReactNode } from 'react';
import { AlertCircle, CheckCircle2, Info, TriangleAlert } from 'lucide-react';

export type AlertTone = 'info' | 'error' | 'warn' | 'success';

const TONES: Record<
  AlertTone,
  { wrap: string; icon: string; title: string; body: string; Icon: typeof Info }
> = {
  info: {
    wrap: 'border-[#e7ddef] bg-[#f6f2fb]',
    icon: 'bg-[#8b5cf6]',
    title: 'text-[#4b0a5b]',
    body: 'text-[#5c4a6e]',
    Icon: Info,
  },
  error: {
    wrap: 'border-[#fecaca] bg-[#fffbfb]',
    icon: 'bg-[#dc2626]',
    title: 'text-[#b91c1c]',
    body: 'text-[#7f1d1d]',
    Icon: AlertCircle,
  },
  warn: {
    wrap: 'border-[#f3d3c2] bg-[#fffaf6]',
    icon: 'bg-[#ff7540]',
    title: 'text-[#b45309]',
    body: 'text-[#7c4a1d]',
    Icon: TriangleAlert,
  },
  success: {
    wrap: 'border-[#b6f0e2] bg-[#e8fbf6]',
    icon: 'bg-[#02b899]',
    title: 'text-[#0f7a63]',
    body: 'text-[#0f5f4e]',
    Icon: CheckCircle2,
  },
};

interface CheckoutAlertProps {
  tone: AlertTone;
  title: string;
  children?: ReactNode;
  /** Buttons/links rendered under the message. */
  actions?: ReactNode;
  /** Compact single-line treatment used inside the form column. */
  compact?: boolean;
  id?: string;
  /** Errors announce immediately; other tones are polite. */
  live?: boolean;
}

export default function CheckoutAlert({
  tone,
  title,
  children,
  actions,
  compact = false,
  id,
  live = true,
}: CheckoutAlertProps) {
  const styles = TONES[tone];
  const { Icon } = styles;

  return (
    <div
      id={id}
      // These alerts mount with their content, so a bare aria-live region is
      // often not announced — an explicit role is.
      role={live ? (tone === 'error' ? 'alert' : 'status') : undefined}
      className={`flex items-start gap-3 rounded-xl border ${styles.wrap} ${
        compact ? 'mb-4 p-3' : 'mb-5 p-4'
      }`}
    >
      <span
        aria-hidden="true"
        className={`flex flex-none items-center justify-center rounded-full text-white ${styles.icon} ${
          compact ? 'h-5 w-5' : 'h-6 w-6'
        }`}
      >
        <Icon className={compact ? 'h-3 w-3' : 'h-3.5 w-3.5'} strokeWidth={3} />
      </span>
      <div className="min-w-0 flex-1">
        <p className={`text-[13.5px] font-bold leading-snug ${styles.title}`}>{title}</p>
        {children ? (
          <div className={`mt-0.5 text-[12.5px] leading-relaxed ${styles.body}`}>{children}</div>
        ) : null}
        {actions ? <div className="mt-3 flex flex-wrap gap-2">{actions}</div> : null}
      </div>
    </div>
  );
}
